/**
 * AssistantProvider - Context provider for the AI Dashboard Assistant
 *
 * Wraps Vercel AI SDK's useChat hook with:
 * - Auth0 token injection for authenticated API requests
 * - Panel open/close state management
 * - Page context injection
 * - Sources-based navigation from assistant responses
 */

import type { DashboardUrl, EntityRef, PageContext } from './tools/definitions';
import {
    DefaultChatTransport,
    lastAssistantMessageIsCompleteWithToolCalls,
} from 'ai';
import {
    ReactNode,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    type SaveResult,
    useDeleteSession,
    useSaveSession,
    useSessionIndex,
} from './sessions/useSessionQueries';
import { type UIMessage, useChat } from '@ai-sdk/react';
import { useHistory, useLocation } from 'react-router-dom';
import { AI_SERVICE_URL } from '@/config/constants';
import type { ChatSessionMeta } from './sessions/types';
import { ENDPOINT } from '@/utils/api/meta.api';
import bugsnagClient from '@/ui/containers/BugsnagClient';
import { buildUrl } from './tools/navigateTo';
import { deriveLabel } from './sessions/utils';
import { fetchConversation } from '@/utils/api/chat.api';
import { mixpanelTrack } from 'utils/app/configureMixpanel';
import { sessionStorage } from './sessions/storage';
import { useAuth0 } from '@auth0/auth0-react';
import useCountryParam from '@/utils/hooks/useCountryParam';
import useCreateReportTool, {
    CREATE_REPORT_CLIENT_TOOLS,
} from '@/ui/views/Reports/useCreateReportTool';
import useSearchableIndustries from '@/utils/hooks/useSearchableIndustries';

// =============================================================================
// Types
// =============================================================================

/** Loose shape for Vercel AI SDK v6 tool-result message parts. */
interface ToolDataPart {
    type: string;
    state?: string;
    input?: Record<string, unknown>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    output?: Record<string, any>;
}

type PanelView = 'chat' | 'history';

interface AssistantContextValue {
    // UI State
    isPanelOpen: boolean;
    panelView: PanelView;
    setPanelView: (view: PanelView) => void;
    openPanel: (view?: PanelView) => void;
    closePanel: () => void;
    togglePanel: () => void;

    // Chat State (from useChat)
    isLoading: boolean;
    messages: UIMessage[];
    error: Error | undefined;

    // Derived State
    currentContent: string;

    // Chat Actions
    sendMessage: (
        content: string,
        meta?: {
            is_suggestion?: boolean;
            is_retry?: boolean;
            is_followup?: boolean;
            follow_up_id?: string;
            skipPageContext?: boolean;
            newChat?: boolean;
            source?: 'panel' | 'homepage' | 'search' | 'entity_bar' | 'reports';
        },
    ) => void;
    resetChat: () => void;
    cancelStream: () => void;

    // Session Management
    activeChatId: string | null;
    sessions: ChatSessionMeta[];
    createNewChat: () => void;
    switchToSession: (sessionId: string) => void;
    deleteSession: (sessionId: string) => void;

    // Model Tier
    modelTier: string | null;
    setModelTier: (tier: string | null) => void;
}

// =============================================================================
// Context
// =============================================================================

const AssistantContext = createContext<AssistantContextValue | null>(null);

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Extract step content from a UIMessage's parts
 */
function getMessageSteps(message: UIMessage) {
    return message.parts
        .filter(
            (part) =>
                !['text', 'data-metadata', 'data-navigation'].includes(
                    part.type,
                ),
        )
        .reduce((steps, part, idx) => {
            if (part.type === 'step-start' && idx !== steps.length - 1) {
                steps.push([]);
            } else if (steps.length) {
                steps[steps.length - 1].push(part);
            }
            return steps;
        }, []);
}

/**
 * Extract text content from a UIMessage's parts
 */
function getMessageText(message: UIMessage): string {
    return message.parts
        .filter(
            (part): part is { type: 'text'; text: string } =>
                part.type === 'text',
        )
        .map((part) => part.text)
        .join('');
}

/**
 * Parse <sources> block from assistant message text.
 * Sources now contain only entity_index + label (no routes).
 */
function parseSources(text: string): EntityRef[] {
    const match = text.match(/<sources>(.*?)<\/sources>/s);
    if (!match) return [];
    try {
        const refs: EntityRef[] = JSON.parse(match[1]);
        return refs.filter((r) => r.label != null);
    } catch {
        return [];
    }
}

/**
 * Parse <follow_ups> block: a JSON array of follow-up IDs the LLM picked
 * from the get_entity_data tool result. Returns [] for missing or malformed.
 */
export function parseFollowUpIds(text: string): string[] {
    const match = text.match(/<follow_ups>(.*?)<\/follow_ups>/s);
    if (!match) return [];
    try {
        const ids = JSON.parse(match[1]);
        if (!Array.isArray(ids)) return [];
        return ids.filter((id): id is string => typeof id === 'string');
    } catch {
        return [];
    }
}

function consolidateSources(
    sources: EntityRef[],
    message: UIMessage,
): EntityRef[] {
    if (sources.length <= 1) return sources;

    const entityDataParts = message.parts.filter(
        (p) =>
            p.type === 'tool-get_entity_data' &&
            (p as ToolDataPart).state === 'output-available' &&
            (p as ToolDataPart).output?.success,
    );

    const grouped = new Map<number | undefined, EntityRef[]>();
    for (const source of sources) {
        const existing = grouped.get(source.entity_index);
        if (existing) {
            existing.push(source);
        } else {
            grouped.set(source.entity_index, [source]);
        }
    }

    const consolidated: EntityRef[] = [];
    for (const [entityIndex, refs] of grouped) {
        if (refs.length === 1) {
            consolidated.push(refs[0]);
            continue;
        }

        // Only consolidate when all tool calls for this entity target the same page
        const matchingUrls = entityDataParts
            .filter(
                (p) => (p as ToolDataPart).input?.entity_index === entityIndex,
            )
            .map(
                (p) =>
                    (p as ToolDataPart).output?.result?.dashboard_url as
                        DashboardUrl | undefined,
            )
            .filter((u): u is DashboardUrl => !!u?.pathname);

        const samePage =
            matchingUrls.length > 1 &&
            matchingUrls.every(
                (u) =>
                    u.pathname === matchingUrls[0].pathname &&
                    u.hash === matchingUrls[0].hash,
            );

        if (!samePage) {
            consolidated.push(...refs);
            continue;
        }

        const labels = refs.map((r) => r.label);
        const colonIdx = labels[0].indexOf(': ');
        let mergedLabel: string;
        if (colonIdx !== -1) {
            const prefix = labels[0].substring(0, colonIdx + 2);
            if (labels.every((l) => l.startsWith(prefix))) {
                const suffixes = labels.map((l) => l.substring(prefix.length));
                mergedLabel = prefix + suffixes.join(' + ');
            } else {
                mergedLabel = labels.join(' + ');
            }
        } else {
            mergedLabel = labels.join(' + ');
        }
        consolidated.push({ entity_index: entityIndex, label: mergedLabel });
    }

    return consolidated;
}

function mergeDashboardUrls(urls: DashboardUrl[]): DashboardUrl {
    if (urls.length <= 1) return urls[0];

    const base = { ...urls[0] };
    const baseParams = new URLSearchParams(base.search.replace(/^\?/, ''));

    const POP_INDEX_KEY = 'popIndexParentName';
    const RELATED_KEY = 'relatedCategories.popIndexParentName';

    const allPopValues = new Set<string>();
    const allRelatedValues = new Set<string>();

    for (const url of urls) {
        const params = new URLSearchParams(url.search.replace(/^\?/, ''));
        const popVal = params.get(POP_INDEX_KEY);
        if (popVal)
            popVal.split(',').forEach((v) => allPopValues.add(v.trim()));
        const relVal = params.get(RELATED_KEY);
        if (relVal)
            relVal.split('||').forEach((v) => allRelatedValues.add(v.trim()));
    }

    if (allPopValues.size > 0) {
        baseParams.set(POP_INDEX_KEY, [...allPopValues].join(','));
    }
    if (allRelatedValues.size > 0) {
        baseParams.set(RELATED_KEY, [...allRelatedValues].join('||'));
    }

    const search = baseParams.toString();
    base.search = search ? `?${search}` : '';
    return base;
}

/**
 * Extract navigation URL from get_entity_data tool results in a message,
 * using the sources ranking to pick the primary target. When multiple tool
 * results match the same entity_index, their dashboard URLs are merged.
 *
 * Returns `entity_index` alongside `url`/`label` so downstream consumers
 * (e.g. inline-visual dispatch) can match the same primary entity without
 * re-deriving it from text + parts.
 */
function extractNavigationFromToolResults(
    message: UIMessage,
    sources: EntityRef[],
): { url: string | null; label: string; entity_index: number | null } | null {
    // Find all completed get_entity_data tool parts with output.
    // Vercel AI SDK v6 uses type `tool-{toolName}` with `input`/`output` props.
    const entityDataParts = message.parts.filter(
        (p) =>
            p.type === 'tool-get_entity_data' &&
            (p as ToolDataPart).state === 'output-available' &&
            (p as ToolDataPart).output?.success,
    );
    if (!entityDataParts.length) return null;

    const primaryIndex = sources[0]?.entity_index;

    const matchingParts =
        primaryIndex != null
            ? entityDataParts.filter(
                  (p) =>
                      (p as ToolDataPart).input?.entity_index === primaryIndex,
              )
            : [];
    const parts = matchingParts.length ? matchingParts : [entityDataParts[0]];

    const rawIndex = (parts[0] as ToolDataPart).input?.entity_index;
    const chosenIndex = typeof rawIndex === 'number' ? rawIndex : null;

    const dashboardUrls = parts
        .map(
            (p) =>
                (p as ToolDataPart).output?.result?.dashboard_url as
                    DashboardUrl | undefined,
        )
        .filter((u): u is DashboardUrl => !!u?.pathname);

    // No linkable page for this result (e.g. cat1 creators/posts — the data is
    // supported but no dashboard view exists). url: null still anchors the inline
    // visual to the primary entity; consumers skip navigation and the banner.
    if (!dashboardUrls.length) {
        return {
            url: null,
            label: sources[0]?.label || '',
            entity_index: chosenIndex,
        };
    }

    const mergedUrl =
        dashboardUrls.length === 1
            ? dashboardUrls[0]
            : mergeDashboardUrls(dashboardUrls);
    return {
        url: buildUrl(mergedUrl),
        label: sources[0]?.label || '',
        entity_index: chosenIndex,
    };
}

// =============================================================================
// Provider Component
// =============================================================================

interface AssistantProviderProps {
    children: ReactNode;
}

export const AssistantProvider = ({ children }: AssistantProviderProps) => {
    // UI State
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [panelView, setPanelView] = useState<PanelView>('chat');
    const [modelTier, setModelTier] = useState<string | null>(null);

    // Session State
    const [activeChatId, setActiveChatId] = useState<string | null>(() =>
        sessionStorage.getActiveId(),
    );
    const { data: sessions = [] } = useSessionIndex();
    const saveSessionMutation = useSaveSession();
    const deleteSessionMutation = useDeleteSession();

    // Tracks the server's updatedAt for the active conversation (optimistic concurrency)
    const serverUpdatedAtRef = useRef<string | null>(null);

    // Hooks
    const { getAccessTokenSilently } = useAuth0();
    const history = useHistory();
    const location = useLocation();

    const { currentCountryParam } = useCountryParam();
    const userIndustries = useSearchableIndustries();

    // Use refs so the transport body closure always reads latest values
    // (transport is memoized once, but body() is called per request)
    const skipPageContextRef = useRef(false);
    const pageContextRef = useRef<PageContext>({
        current_route: location.pathname,
        page_title: document.title,
    });
    pageContextRef.current = {
        current_route: location.pathname,
        page_title: document.title,
    };

    const geoRef = useRef<string | null>(currentCountryParam);
    geoRef.current = currentCountryParam;

    const userIndustriesRef = useRef<string[] | null>(userIndustries);
    userIndustriesRef.current = userIndustries;

    const modelTierRef = useRef<string | null>(modelTier);
    modelTierRef.current = modelTier;

    const transport = useMemo(
        () =>
            new DefaultChatTransport({
                api: `${AI_SERVICE_URL}/ai/assistant/`,
                headers: async () => {
                    const token = await getAccessTokenSilently();
                    return {
                        Authorization: `Bearer ${token}`,
                    };
                },
                body: () => {
                    const skipContext = skipPageContextRef.current;
                    skipPageContextRef.current = false;
                    return {
                        pageContext: skipContext
                            ? null
                            : pageContextRef.current,
                        geo: geoRef.current,
                        userIndustries: userIndustriesRef.current,
                        modelTier: modelTierRef.current,
                        clientTools: CREATE_REPORT_CLIENT_TOOLS,
                    };
                },
            }),
        [getAccessTokenSilently],
    );

    const {
        messages,
        sendMessage: chatSendMessage,
        status,
        error,
        stop,
        setMessages,
        addToolOutput,
    } = useChat({
        transport,

        // Auto-resubmit after client tool results are provided
        sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithToolCalls,

        // Handle client-side tool execution
        onFinish: ({ message }) => {
            if (message.role !== 'assistant') return;
            const text = getMessageText(message);
            const sources = consolidateSources(parseSources(text), message);
            if (!sources.length) return;

            const nav = extractNavigationFromToolResults(message, sources);
            if (!nav) return;

            setMessages((prev) =>
                prev.map((msg) =>
                    msg.id === message.id
                        ? {
                              ...msg,
                              parts: [
                                  ...msg.parts,
                                  {
                                      type: 'data-navigation',
                                      data: {
                                          label: nav.label,
                                          url: nav.url,
                                          entity_index: nav.entity_index,
                                      },
                                  },
                              ],
                          }
                        : msg,
                ),
            );

            // Navigate only when the event is freshly produced. onFinish fires
            // once, on live stream completion — never when a saved chat is loaded
            // from memory — so we don't replay navigation on reopen (the persisted
            // data-navigation part above still renders the "Navigated to" banner).
            // url-less parts (no dashboard page) anchor the visual but don't navigate.
            const { pathname, search, hash } = history.location;
            if (nav.url && nav.url !== pathname + search + hash)
                history.push(nav.url);
        },

        onError: (err) => {
            console.error('Assistant error:', err);
            mixpanelTrack('AI Assistant Error', {
                error_message: err?.message || String(err),
            });
        },
    });

    // Drop a trailing assistant message with no text (e.g. aborted before any content arrived)
    const stripEmptyTrailingAssistant = (msgs: UIMessage[]): UIMessage[] => {
        if (!msgs.length) return msgs;
        const last = msgs[msgs.length - 1];
        if (last.role === 'assistant' && getMessageText(last).trim() === '') {
            return msgs.slice(0, -1);
        }
        return msgs;
    };

    // Always-fresh ref to messages — switch handlers read this instead of stale closures
    const messagesRef = useRef(messages);
    messagesRef.current = messages;

    // Tracks which chatId owns the active stream (consumed + cleared on auto-save)
    const streamChatIdRef = useRef<string | null>(null);

    const isLoading = status === 'streaming' || status === 'submitted';

    // Set when the user (or a chat switch) aborts the stream. The settle that
    // follows an abort must not execute pending client tools — the turn was
    // cancelled, and during a chat switch the messages still briefly hold the
    // abandoned conversation.
    const streamAbortedRef = useRef(false);
    const stopStream = useCallback(() => {
        streamAbortedRef.current = true;
        stop();
    }, [stop]);
    const wasStreamAborted = useCallback(() => streamAbortedRef.current, []);

    // Execute the model's create_report tool calls (the transport opts this
    // chat in via clientTools). Auto-navigation is a reports-pages policy: a
    // build watched from /reports(/new) lands on the finished report
    // (/reports/new is a dead placeholder — replaced, not stacked); anywhere
    // else the chat's chip is the affordance, so a long build never yanks the
    // user out of unrelated work.
    useCreateReportTool({
        messages,
        status,
        addToolOutput,
        wasAborted: wasStreamAborted,
        source: 'assistant_tool',
        onCreated: (_report: unknown, output: { url: string }) => {
            const { pathname } = history.location;
            if (pathname === '/reports/new') history.replace(output.url);
            else if (pathname === '/reports') history.push(output.url);
        },
        onFailed: (err: unknown) => {
            bugsnagClient.notify(err as Error);
            mixpanelTrack('AI Report Create Failed', {
                source: 'assistant_tool',
            });
        },
    });

    // UI Actions
    // Callers open the panel to show a chat they just started, so a history
    // view left over from last time must not cover it.
    const openPanel = useCallback((view: PanelView = 'chat') => {
        setPanelView(view);
        setIsPanelOpen(true);
        mixpanelTrack('AI Assistant Open');
    }, []);
    const closePanel = useCallback(() => {
        setIsPanelOpen(false);
        mixpanelTrack('AI Assistant Close');
    }, []);
    const togglePanel = useCallback(() => {
        setIsPanelOpen((prev) => {
            mixpanelTrack(prev ? 'AI Assistant Close' : 'AI Assistant Open');
            return !prev;
        });
    }, []);

    // ---- Session Helpers ----

    const saveCurrentSession = useCallback(
        (msgs: UIMessage[], chatId: string | null) => {
            if (!chatId || msgs.length === 0) return;
            const now = Date.now();
            const existing = sessions.find((s) => s.id === chatId);
            const meta: ChatSessionMeta = {
                id: chatId,
                label: deriveLabel(msgs),
                createdAt: existing?.createdAt ?? now,
                updatedAt: now,
            };
            saveSessionMutation.mutate(
                {
                    id: chatId,
                    messages: msgs,
                    meta,
                    updatedAt: serverUpdatedAtRef.current,
                },
                {
                    onSuccess: (result: SaveResult) => {
                        if (result.conflict) {
                            // Stale write detected — adopt server state
                            const serverMsgs = (result.conflict.messages ??
                                []) as UIMessage[];
                            setMessages(serverMsgs);
                            serverUpdatedAtRef.current =
                                result.conflict.updatedAt ?? null;
                        } else if (result.updatedAt) {
                            serverUpdatedAtRef.current = result.updatedAt;
                        }
                    },
                },
            );
        },
        [sessions, saveSessionMutation, setMessages],
    );

    // Ensure an active session exists — lazily create one before first message
    const ensureActiveSession = useCallback((): string => {
        if (activeChatId) return activeChatId;
        const newId = crypto.randomUUID();
        setActiveChatId(newId);
        sessionStorage.setActiveId(newId);
        return newId;
    }, [activeChatId]);

    // Refs for startFreshSession's unstable inputs (saveCurrentSession is
    // rebuilt every render via the react-query mutation). startFreshSession
    // feeds sendMessage's deps, and sendMessage must keep a stable identity —
    // consumers memoize and key effects on it, so identity churn here re-fires
    // those on every streamed token.
    const saveCurrentSessionRef = useRef(saveCurrentSession);
    saveCurrentSessionRef.current = saveCurrentSession;
    const activeChatIdRef = useRef(activeChatId);
    activeChatIdRef.current = activeChatId;
    const isLoadingRef = useRef(isLoading);
    isLoadingRef.current = isLoading;

    // Save the current session and switch to a fresh empty one, returning the
    // new chat id synchronously — callers that immediately send a message
    // (sendMessage with newChat) need the id in the same tick, before the
    // activeChatId state update lands.
    const startFreshSession = useCallback((): string => {
        if (isLoadingRef.current) stopStream();
        saveCurrentSessionRef.current(
            stripEmptyTrailingAssistant(messagesRef.current),
            activeChatIdRef.current,
        );
        const id = crypto.randomUUID();
        setActiveChatId(id);
        sessionStorage.setActiveId(id);
        setMessages([]);
        serverUpdatedAtRef.current = null;
        return id;
    }, [stopStream, setMessages]);

    // Chat Actions
    const sendMessage = useCallback(
        (
            content: string,
            meta?: {
                is_suggestion?: boolean;
                is_retry?: boolean;
                is_followup?: boolean;
                follow_up_id?: string;
                skipPageContext?: boolean;
                newChat?: boolean;
                source?:
                    'panel' | 'homepage' | 'search' | 'entity_bar' | 'reports';
            },
        ) => {
            if (meta?.skipPageContext) {
                skipPageContextRef.current = true;
            }
            const chatId = meta?.newChat
                ? startFreshSession()
                : ensureActiveSession();
            streamChatIdRef.current = chatId;
            streamAbortedRef.current = false;
            // messagesRef lags the fresh session's synchronous clear until the
            // next render, so a newChat send is first by definition.
            const isFirstMessage =
                meta?.newChat || messagesRef.current.length === 0;
            mixpanelTrack('AI Assistant Message Sent', {
                message_length: content.length,
                model_tier: modelTierRef.current ?? 'auto',
                is_suggestion: meta?.is_suggestion ?? false,
                is_retry: meta?.is_retry ?? false,
                is_followup: meta?.is_followup ?? false,
                follow_up_id: meta?.follow_up_id,
                ...(isFirstMessage && { source: meta?.source ?? 'panel' }),
                chat_id: chatId,
            });
            chatSendMessage({ text: content });
        },
        [chatSendMessage, ensureActiveSession, startFreshSession],
    );

    const cancelStream = useCallback(() => {
        stopStream();
    }, [stopStream]);

    const createNewChat = useCallback(() => {
        mixpanelTrack('AI Assistant New Chat', {
            previous_message_count: messagesRef.current.length,
            chat_id: activeChatId,
        });
        startFreshSession();
    }, [activeChatId, startFreshSession]);

    const switchToSession = useCallback(
        async (sessionId: string) => {
            // Skip if already viewing this session with messages loaded
            if (sessionId === activeChatId && messagesRef.current.length > 0)
                return;

            mixpanelTrack('AI Assistant Chat Opened', { chat_id: sessionId });

            if (sessionId !== activeChatId) {
                if (isLoading) stopStream();
                saveCurrentSession(
                    stripEmptyTrailingAssistant(messagesRef.current),
                    activeChatId,
                );
                setActiveChatId(sessionId);
                sessionStorage.setActiveId(sessionId);
            }

            serverUpdatedAtRef.current = null;
            try {
                const res = await fetchConversation(sessionId);
                const loaded = (res?.messages ?? []) as UIMessage[];
                setMessages(loaded);
                serverUpdatedAtRef.current = res?.updatedAt ?? null;
            } catch {
                setMessages([]);
            }
        },
        [activeChatId, isLoading, stopStream, saveCurrentSession, setMessages],
    );

    const deleteSessionAction = useCallback(
        async (sessionId: string) => {
            mixpanelTrack('AI Assistant Chat Deleted', { chat_id: sessionId });
            deleteSessionMutation.mutate(sessionId);
            if (sessionId === activeChatId) {
                const remaining = sessions.filter((s) => s.id !== sessionId);
                if (remaining.length > 0) {
                    const next = remaining[0];
                    setActiveChatId(next.id);
                    sessionStorage.setActiveId(next.id);
                    serverUpdatedAtRef.current = null;
                    try {
                        const res = await fetchConversation(next.id);
                        const loaded = (res?.messages ?? []) as UIMessage[];
                        setMessages(loaded);
                        serverUpdatedAtRef.current = res?.updatedAt ?? null;
                    } catch {
                        setMessages([]);
                    }
                } else {
                    const newId = crypto.randomUUID();
                    setActiveChatId(newId);
                    sessionStorage.setActiveId(newId);
                    setMessages([]);
                    serverUpdatedAtRef.current = null;
                }
            }
        },
        [deleteSessionMutation, activeChatId, sessions, setMessages],
    );

    const resetChat = useCallback(() => {
        createNewChat();
    }, [createNewChat]);

    // Auto-save: persist when the stream settles. Detected as the transition
    // out of streaming/submitted rather than by a resting status string —
    // SDK v6 rests at 'ready' (the old 'idle' comparison never matched, so
    // this save only ever happened via chat-switch/new-chat/unload).
    const prevStatusRef = useRef(status);
    useEffect(() => {
        const wasActive =
            prevStatusRef.current === 'streaming' ||
            prevStatusRef.current === 'submitted';
        const isActive = status === 'streaming' || status === 'submitted';
        prevStatusRef.current = status;
        if (wasActive && !isActive && status !== 'error') {
            const targetId = streamChatIdRef.current ?? activeChatId;
            streamChatIdRef.current = null;
            if (targetId && messages.length > 0) {
                saveCurrentSession(
                    stripEmptyTrailingAssistant(messages),
                    targetId,
                );
            }
        }
    }, [status, activeChatId, messages, saveCurrentSession]);

    // Save on page unload (tab close, navigation away)
    useEffect(() => {
        const handleBeforeUnload = () => {
            const msgs = messagesRef.current;
            const chatId = activeChatId;
            if (!chatId || msgs.length === 0) return;

            const cleaned = stripEmptyTrailingAssistant(msgs);
            fetch(`${ENDPOINT}/chat/conversations/`, {
                method: 'POST',
                keepalive: true,
                body: JSON.stringify({
                    id: chatId,
                    label: deriveLabel(cleaned),
                    messages: cleaned,
                    updatedAt: serverUpdatedAtRef.current,
                }),
            });
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () =>
            window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [activeChatId]);

    // Restore messages on mount via direct fetch (fires once)
    const didRestoreRef = useRef(false);
    useEffect(() => {
        if (didRestoreRef.current || !activeChatId) return;
        didRestoreRef.current = true;
        fetchConversation(activeChatId)
            .then((res) => {
                const loaded = (res?.messages ?? []) as UIMessage[];
                if (loaded.length > 0) setMessages(loaded);
                serverUpdatedAtRef.current = res?.updatedAt ?? null;
            })
            .catch(() => {
                // 404 or network error for new/missing chat — no-op
            });
    }, []);

    // Derived state
    const currentContent = useMemo(() => {
        const lastAssistant = [...messages]
            .reverse()
            .find((m) => m.role === 'assistant');
        return lastAssistant ? getMessageText(lastAssistant) : '';
    }, [messages]);

    // Context value
    const value: AssistantContextValue = useMemo(
        () => ({
            isPanelOpen,
            panelView,
            setPanelView,
            openPanel,
            closePanel,
            togglePanel,
            isLoading,
            messages,
            error,
            currentContent,
            sendMessage,
            resetChat,
            cancelStream,
            activeChatId,
            sessions,
            createNewChat,
            switchToSession,
            deleteSession: deleteSessionAction,
            modelTier,
            setModelTier,
        }),
        [
            isPanelOpen,
            panelView,
            openPanel,
            closePanel,
            togglePanel,
            isLoading,
            messages,
            error,
            currentContent,
            sendMessage,
            resetChat,
            cancelStream,
            activeChatId,
            sessions,
            createNewChat,
            switchToSession,
            deleteSessionAction,
            modelTier,
            setModelTier,
        ],
    );

    return (
        <AssistantContext.Provider value={value}>
            {children}
        </AssistantContext.Provider>
    );
};

// =============================================================================
// Hook
// =============================================================================

export const useAssistantContext = () => {
    const context = useContext(AssistantContext);

    if (!context) {
        throw new Error(
            'useAssistantContext must be used within an AssistantProvider',
        );
    }

    return context;
};

// =============================================================================
// Helper Exports
// =============================================================================

export { getMessageSteps, getMessageText };

/**
 * Check if a message has pending tool calls
 */
export const hasPendingToolCalls = (message: UIMessage): boolean => {
    return message.parts.some(
        (part) =>
            part.type === 'tool-invocation' &&
            !['output-available', 'output-error'].includes(
                (part as { state?: string }).state ?? '',
            ),
    );
};

export default AssistantProvider;
